import React, { useState, useEffect } from 'react';
import { 
  Users, 
  BookOpen, 
  FileText, 
  Save, 
  ArrowLeft, 
  ArrowRight,
  CheckCircle,
  X,
  Loader,
  AlertCircle
} from 'lucide-react';

import PessoaSelectorMultiple from '../components/PessoaSelectorMultiple';
import CursoSelector from '../components/CursoSelector';
import matriculaService from '../services/matriculaService';

// Versão com suporte a matrícula em massa
const MatriculaCreate = ({ onSave, onCancel, initialData = null }) => {
  // Estados básicos
  const [currentStep, setCurrentStep] = useState(1);
  const [salvando, setSalvando] = useState(false);
  const [resultadoMassa, setResultadoMassa] = useState(null);

  // Prevenir scroll da página de fundo quando modal está aberto
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);
  
  // Dados do formulário - estrutura com múltiplas pessoas
  const [formData, setFormData] = useState({
    pessoas: initialData?.pessoas || [],
    curso: initialData?.curso || null,
    dataMatricula: initialData?.dataMatricula || new Date().toISOString().split('T')[0],
    observacoes: initialData?.observacoes || '',
    tipoCurso: initialData?.tipoCurso || '',
    periodo: initialData?.periodo || ''
  });

  // Configuração dos steps
  const steps = [
    {
      id: 1,
      title: 'Selecionar Pessoas',
      description: 'Escolha as pessoas que serão matriculadas',
      icon: Users
    },
    {
      id: 2,
      title: 'Selecionar Curso',
      description: 'Escolha o curso para matrícula',
      icon: BookOpen
    },
    {
      id: 3,
      title: 'Dados da Matrícula',
      description: 'Complete as informações da matrícula',
      icon: FileText
    }
  ];
  
  // Steps para exibição (não incluir step 4 de resultados)
  const displaySteps = steps;

  // Atualizar dados do formulário
  const updateFormData = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Navegação entre steps
  const nextStep = () => {
    if (currentStep < steps.length) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  // Validação básica do step atual
  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return formData.pessoas && formData.pessoas.length > 0;
      case 2:
        return !!formData.curso;
      case 3:
        return !!formData.dataMatricula && !!formData.tipoCurso && !!formData.periodo;
      default:
        return true;
    }
  };

  // Salvar matrícula (em massa)
  const handleSave = async () => {
    if (!canProceed()) return;

    setSalvando(true);
    setResultadoMassa(null);
    
    try {
      console.log('FormData completo:', formData);
      console.log('Pessoas selecionadas:', formData.pessoas);
      console.log('Curso selecionado:', formData.curso);
      
      const idCurso = formData.curso.idCurso || formData.curso.id;
      
      if (!idCurso) {
        throw new Error('ID do curso não encontrado');
      }

      // Extrair IDs das pessoas
      const idsPessoas = formData.pessoas.map(pessoa => pessoa.idPessoa || pessoa.id);
      
      if (idsPessoas.length === 0 || idsPessoas.some(id => !id)) {
        throw new Error('IDs das pessoas não encontrados');
      }
      
      const dadosMatricula = {
        idsPessoas: idsPessoas,
        idCurso: idCurso,
        dataMatricula: formData.dataMatricula,
        observacoes: formData.observacoes,
        tipoCurso: formData.tipoCurso,
        periodo: formData.periodo
      };

      console.log('Dados da matrícula em massa a serem enviados:', dadosMatricula);

      const response = await matriculaService.criarMatriculaMassa(dadosMatricula);
      
      if (response.success) {
        // Se todas foram bem-sucedidas
        if (response.data.erros.length === 0) {
          alert(`✅ Matrículas criadas com sucesso!\n\n${response.data.sucesso.length} pessoa(s) matriculada(s).`);
          onSave?.(response.data);
        } else {
          // Parcialmente bem-sucedido
          setResultadoMassa(response.data);
          // Avançar para uma tela de resultado ao invés de fechar
          setCurrentStep(4); // Vamos criar um step 4 para mostrar resultados
        }
      } else {
        alert(response.error || 'Erro ao salvar matrículas');
      }
    } catch (error) {
      console.error('Erro ao salvar matrículas:', error);
      alert('Erro inesperado ao salvar: ' + error.message);
    } finally {
      setSalvando(false);
    }
  };

  // Renderizar conteúdo do step atual
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-4">
            <PessoaSelectorMultiple
              selectedPessoas={formData.pessoas}
              onSelectPessoas={(pessoas) => updateFormData('pessoas', pessoas)}
              placeholder="Buscar pessoas por nome, CPF ou RG..."
            />
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <CursoSelector
              selectedCurso={formData.curso}
              onSelectCurso={(curso) => updateFormData('curso', curso)}
              placeholder="Buscar curso por nome..."
            />
            {formData.curso && (
              <div className="mt-4 p-3 sm:p-4 bg-green-50 border border-green-200 rounded-lg">
                <h4 className="font-semibold text-green-800 mb-2 text-sm sm:text-base">Curso Selecionado:</h4>
                <div className="text-xs sm:text-sm text-green-700 space-y-1">
                  <div><strong>Nome:</strong> {formData.curso.nome}</div>
                  {formData.curso.descricao && (
                    <div><strong>Descrição:</strong> {formData.curso.descricao}</div>
                  )}
                  {formData.curso.carga_horaria && (
                    <div><strong>Carga Horária:</strong> {formData.curso.carga_horaria}h</div>
                  )}
                </div>
              </div>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            
            <div className="bg-gray-50 p-3 sm:p-4 rounded-lg mb-4">
              <h4 className="font-semibold text-gray-800 mb-3 text-sm sm:text-base">Resumo da Matrícula</h4>
              <div className="grid grid-cols-1 gap-3 text-xs sm:text-sm">
                <div className="p-2 bg-white rounded border-l-4 border-blue-400">
                  <span className="text-gray-600">Pessoas selecionadas:</span>
                  <p className="font-medium break-words">{formData.pessoas?.length || 0} pessoa(s)</p>
                  {formData.pessoas && formData.pessoas.length <= 5 && (
                    <ul className="mt-2 space-y-1 text-xs">
                      {formData.pessoas.map((pessoa, idx) => (
                        <li key={idx} className="text-gray-600">• {pessoa.nome}</li>
                      ))}
                    </ul>
                  )}
                  {formData.pessoas && formData.pessoas.length > 5 && (
                    <p className="mt-2 text-xs text-gray-500">
                      {formData.pessoas.slice(0, 3).map(p => p.nome).join(', ')} e mais {formData.pessoas.length - 3}...
                    </p>
                  )}
                </div>
                <div className="p-2 bg-white rounded border-l-4 border-green-400">
                  <span className="text-gray-600">Curso:</span>
                  <p className="font-medium break-words">{formData.curso?.nome}</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="sm:col-span-2 lg:col-span-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data da Matrícula *
                </label>
                <input
                  type="date"
                  value={formData.dataMatricula}
                  onChange={(e) => updateFormData('dataMatricula', e.target.value)}
                  className="w-full p-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Curso *
                </label>
                <select
                  value={formData.tipoCurso}
                  onChange={(e) => updateFormData('tipoCurso', e.target.value)}
                  className="w-full p-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Selecione o tipo</option>
                  <option value="Presencial">Presencial</option>
                  <option value="Distância">Distância</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Período *
                </label>
                <select
                  value={formData.periodo}
                  onChange={(e) => updateFormData('periodo', e.target.value)}
                  className="w-full p-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Selecione o período</option>
                  <option value="Semanal">Semanal</option>
                  <option value="Aos Sábados">Aos Sábados</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Observações
              </label>
              <textarea
                value={formData.observacoes}
                onChange={(e) => updateFormData('observacoes', e.target.value)}
                className="w-full p-3 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                rows={3}
                placeholder="Observações adicionais sobre a matrícula..."
              />
            </div>
          </div>
        );

      case 4:
        // Step de resultados (após processamento)
        return (
          <div className="space-y-4">
            <div className="text-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Resultado do Processamento</h3>
              <p className="text-sm text-gray-600 mt-1">Matrículas processadas</p>
            </div>

            {resultadoMassa && (
              <>
                {/* Resumo */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="bg-blue-50 p-3 rounded-lg text-center">
                    <div className="text-2xl font-bold text-blue-600">{resultadoMassa.total}</div>
                    <div className="text-xs text-gray-600">Total</div>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg text-center">
                    <div className="text-2xl font-bold text-green-600">{resultadoMassa.sucesso.length}</div>
                    <div className="text-xs text-gray-600">Sucesso</div>
                  </div>
                  <div className="bg-yellow-50 p-3 rounded-lg text-center border border-yellow-200">
                    <div className="text-2xl font-bold text-yellow-600">{resultadoMassa.erros.length}</div>
                    <div className="text-xs text-gray-600">Não Realizadas</div>
                  </div>
                </div>

                {/* Lista de sucessos */}
                {resultadoMassa.sucesso.length > 0 && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h4 className="font-semibold text-green-800 mb-2 flex items-center">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Matrículas Criadas com Sucesso ({resultadoMassa.sucesso.length})
                    </h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {resultadoMassa.sucesso.map((item, idx) => (
                        <div key={idx} className="bg-white p-2 rounded text-sm">
                          <div className="font-medium text-gray-900">
                            {item.matricula?.nomeAluno || `Pessoa ${item.idPessoa}`}
                          </div>
                          <div className="text-xs text-green-600">
                            Matrícula: {item.matricula?.numeroMatricula}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Lista de erros/impedimentos */}
                {resultadoMassa.erros.length > 0 && (
                  <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4">
                    <h4 className="font-semibold text-yellow-800 mb-2 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-2" />
                      Matrículas Não Realizadas ({resultadoMassa.erros.length})
                    </h4>
                    <p className="text-xs text-yellow-700 mb-3">
                      As matrículas abaixo não foram processadas devido a validações de negócio:
                    </p>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {resultadoMassa.erros.map((item, idx) => {
                        // Priorizar nome vindo do backend, depois buscar no formulário
                        let nomePessoa = item.nomePessoa;
                        
                        if (!nomePessoa) {
                          const pessoa = formData.pessoas.find(p => 
                            p.idPessoa === item.idPessoa || 
                            p.id === item.idPessoa
                          );
                          nomePessoa = pessoa?.nome || pessoa?.nomeCompleto;
                        }
                        
                        return (
                          <div key={idx} className="bg-white border border-yellow-200 p-3 rounded text-sm">
                            <div className="font-medium text-gray-900 mb-1">
                              {nomePessoa || `Pessoa ID: ${item.idPessoa}`}
                            </div>
                            <div className="flex items-start text-xs text-yellow-700">
                              <span className="font-semibold mr-1">Motivo:</span>
                              <span>{item.erro}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg max-w-4xl w-full mx-auto h-[85vh] max-h-[700px] overflow-hidden flex flex-col">
      {/* Header */}
      <div className="px-4 sm:px-6 py-3 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
        <div>
          <h2 className="text-lg sm:text-xl font-bold text-gray-800">Nova Matrícula</h2>
          <p className="text-sm text-gray-600">
            {currentStep <= displaySteps.length ? `Step ${currentStep} de ${displaySteps.length}` : 'Resultado'}
          </p>
        </div>
        <button
          onClick={onCancel}
          className="text-gray-500 hover:text-gray-700 transition-colors p-1"
        >
          <X className="w-5 h-5 sm:w-6 sm:h-6" />
        </button>
      </div>

      {/* Progress - Mobile Optimized (não mostrar se estiver no step 4 de resultados) */}
      {currentStep <= displaySteps.length && (
        <div className="px-4 sm:px-6 py-2 sm:py-3 bg-gray-50 flex-shrink-0">
          <div className="flex items-center justify-between">
            {displaySteps.map((step, index) => {
              const isActive = currentStep === step.id;
              const isCompleted = currentStep > step.id;
              const Icon = step.icon;

              return (
                <div key={step.id} className="flex items-center">
                  <div className={`
                    flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 transition-all
                    ${isActive ? 'border-blue-600 bg-blue-600 text-white' : ''}
                    ${isCompleted ? 'border-green-600 bg-green-600 text-white' : ''}
                    ${!isActive && !isCompleted ? 'border-gray-300 bg-white text-gray-400' : ''}
                  `}>
                    {isCompleted ? (
                      <CheckCircle className="w-4 h-4 sm:w-6 sm:h-6" />
                    ) : (
                      <Icon className="w-3 h-3 sm:w-5 sm:h-5" />
                    )}
                  </div>
                  {index < displaySteps.length - 1 && (
                    <div className={`
                      h-1 w-8 sm:w-16 mx-1 sm:mx-2 transition-all
                      ${isCompleted ? 'bg-green-600' : 'bg-gray-300'}
                    `} />
                  )}
                </div>
              );
            })}
          </div>
          
          {/* Step Title for Mobile */}
          <div className="mt-2 sm:hidden text-center">
            <h3 className="text-sm font-medium text-gray-700">
              {displaySteps[currentStep - 1]?.title}
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              {displaySteps[currentStep - 1]?.description}
            </p>
          </div>
        </div>
      )}

      {/* Content - Scrollable with more space */}
      <div className="px-4 sm:px-6 py-4 flex-1 overflow-y-auto min-h-0">
        {renderStepContent()}
      </div>

      {/* Actions - Fixed Bottom */}
      <div className="px-4 sm:px-6 py-3 bg-gray-50 border-t border-gray-200 flex justify-between flex-shrink-0">
        {currentStep === 4 ? (
          // Step de resultado - apenas botão de fechar
          <div className="w-full flex justify-center">
            <button
              onClick={() => {
                if (resultadoMassa && resultadoMassa.sucesso.length > 0) {
                  onSave?.(resultadoMassa);
                } else {
                  onCancel();
                }
              }}
              className="flex items-center px-6 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Concluir
            </button>
          </div>
        ) : (
          <>
            <button
              onClick={prevStep}
              disabled={currentStep === 1}
              className="flex items-center px-3 sm:px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Anterior</span>
              <span className="sm:hidden">Ant.</span>
            </button>

            <div className="flex space-x-2 sm:space-x-3">
              {currentStep === displaySteps.length ? (
                <button
                  onClick={handleSave}
                  disabled={!canProceed() || salvando}
                  className="flex items-center px-4 sm:px-6 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                 aria-label="Salvar Matrículas">
                  {salvando ? (
                    <>
                      <Loader className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2 animate-spin" />
                      <span className="hidden sm:inline">Salvando...</span>
                      <span className="sm:hidden">...</span>
                    </>
                  ) : (
                    <>
                      <Save className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                      <span className="hidden sm:inline">Salvar Matrículas</span>
                      <span className="sm:hidden">Salvar</span>
                    </>
                  )}
                </button>
              ) : (
                <button
                  onClick={nextStep}
                  disabled={!canProceed()}
                  className="flex items-center px-4 sm:px-6 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <span className="hidden sm:inline">Próximo</span>
                  <span className="sm:hidden">Próx.</span>
                  <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1 sm:ml-2" />
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MatriculaCreate;